import { useEffect, useId, useRef, useState, type ReactNode } from 'react';
import { Box, Button, Typography } from '@mui/material';
import { visuallyHidden } from '@mui/utils';
import { CheckIcon, ChevronDownIcon } from '@heroicons/react/24/outline';
import { useTranslation } from 'react-i18next';

import {
  ANSWER_LENGTHS,
  ANSWER_WORDINGS,
  exampleAnswerKey,
  lengthHintKey,
  lengthLabelKey,
  wordingHintKey,
  wordingLabelKey,
  type AnswerStyle,
} from '@/components/chat/AnswerStyle';
import useAnalytics from '@/hooks/useAnalytics';
import type { AnswerStyleSource } from '@/hooks/useAnswerStyleActions';
import {
  BORDER_RADIUS,
  BORDER_RADIUS_CIRCLE,
  BORDER_RADIUS_PILL,
  primitiveColors,
  semanticColors,
} from '@/theme/tokens';

import { SECTION_LABEL_SX, optionCardSx } from './answerStyle.styles';

interface AnswerStyleEditorProps {
  value: AnswerStyle;
  onChange: (next: AnswerStyle) => void;
  /** Where this editor is shown, for its analytics. */
  source: AnswerStyleSource;
  /** The example starts collapsed, to keep the first read short. */
  initiallyShowExample?: boolean;
  /** Roomier type and targets for the desktop settings dialog. */
  comfortable?: boolean;
  /** Settings draft feedback and actions, before the expandable example. */
  afterChoices?: ReactNode;
  /** Optional desktop settings actions beside the example toggle. */
  exampleToolbarActions?: ReactNode;
  /** Desktop persistence guidance and feedback before the expanded example. */
  beforeExample?: ReactNode;
}

const QUESTION_SX = {
  p: 0,
  mb: '8px',
  fontSize: 13,
  fontWeight: 600,
  lineHeight: 1.35,
  color: semanticColors.textPrimary,
} as const;

/** The selected card's mark: a small brand disc with a check. */
function CheckBadge() {
  return (
    <Box
      aria-hidden="true"
      sx={{
        position: 'absolute',
        top: 10,
        insetInlineEnd: 10,
        width: 16,
        height: 16,
        borderRadius: BORDER_RADIUS_CIRCLE,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: primitiveColors.rockSlate,
        color: semanticColors.textOnPrimary,
      }}
    >
      <CheckIcon style={{ width: 11, height: 11, strokeWidth: 3 }} />
    </Box>
  );
}

/** An unselected option's mark: an empty ring, so the card reads as a choice. */
function EmptyRing() {
  return (
    <Box
      aria-hidden="true"
      sx={{
        position: 'absolute',
        top: 10,
        insetInlineEnd: 10,
        width: 16,
        height: 16,
        borderRadius: BORDER_RADIUS_CIRCLE,
        border: `1.5px solid ${semanticColors.inputBorderHover}`,
      }}
    />
  );
}

interface OptionCardProps {
  name: string;
  value: string;
  checked: boolean;
  label: string;
  hint: string;
  hintSize: number;
  comfortable: boolean;
  onSelect: () => void;
}

/** One option: a native radio (keyboard and screen-reader semantics) inside a card. */
function OptionCard({
  name,
  value,
  checked,
  label,
  hint,
  hintSize,
  comfortable,
  onSelect,
}: OptionCardProps) {
  return (
    <Box
      component="label"
      sx={{
        ...optionCardSx(checked),
        ...(comfortable
          ? { minHeight: 84, padding: '12px', paddingInlineEnd: '36px', gap: '4px' }
          : {}),
      }}
    >
      <Box
        component="input"
        type="radio"
        name={name}
        value={value}
        checked={checked}
        onChange={onSelect}
        sx={visuallyHidden}
      />
      {checked ? <CheckBadge /> : <EmptyRing />}
      <Typography
        component="span"
        sx={{
          fontSize: comfortable ? 14 : 13,
          fontWeight: 600,
          lineHeight: 1.35,
          color: semanticColors.textPrimary,
        }}
      >
        {label}
      </Typography>
      <Typography
        component="span"
        sx={{
          fontSize: hintSize,
          lineHeight: comfortable ? 1.5 : 1.4,
          color: semanticColors.textTertiary,
        }}
      >
        {hint}
      </Typography>
    </Box>
  );
}

/**
 * The two answer-style questions and the local example, shared by the
 * onboarding dialog and Settings. Mirrors the mobile `AnswerStyleEditor`.
 *
 * Two separate choices, each a native radio group in a fieldset — arrow keys
 * move within a group and a screen reader names the question — drawn as equal
 * cards so no option reads as the better one: same size, same weight, no
 * badge but the selection check, no meter. The example is six fixed,
 * editorially written texts (never a model call), so changing a choice changes
 * the sample instantly and nothing else.
 */
export default function AnswerStyleEditor({
  value,
  onChange,
  source,
  initiallyShowExample = false,
  comfortable = false,
  afterChoices,
  exampleToolbarActions,
  beforeExample,
}: AnswerStyleEditorProps) {
  const { t } = useTranslation('common');
  const { track } = useAnalytics();
  const [showExample, setShowExample] = useState(initiallyShowExample);
  const [announcement, setAnnouncement] = useState('');
  const baseId = useId();
  const exampleId = `${baseId}-example`;
  const summary = t('answerStyle.summary', {
    wording: t(wordingLabelKey(value.wording)),
    length: t(lengthLabelKey(value.length)),
  });

  // Announce the refreshed example politely, only after the voter changes a
  // choice while it is open — not on first render.
  const lastAnnounced = useRef(summary);
  useEffect(() => {
    if (!showExample || lastAnnounced.current === summary) return;
    lastAnnounced.current = summary;
    setAnnouncement(t('answerStyle.example.updated', { summary }));
  }, [showExample, summary, t]);

  const toggleExample = () => {
    const next = !showExample;
    setShowExample(next);
    lastAnnounced.current = summary;
    track('Answer Style Example Toggled', { source, expanded: next });
  };

  const paragraphs = t(exampleAnswerKey(value)).split('\n\n');

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: '18px' }}>
      <Box component="fieldset" sx={{ border: 0, m: 0, p: 0, minWidth: 0 }}>
        <Typography component="legend" sx={[QUESTION_SX, comfortable ? { fontSize: 14 } : {}]}>
          {t('answerStyle.wording.question')}
        </Typography>
        <Box
          sx={{
            display: 'grid',
            // Laid out like the length choices below: three across where there
            // is room, stacked rows on a phone.
            gridTemplateColumns: { xs: 'minmax(0, 1fr)', sm: 'repeat(3, minmax(0, 1fr))' },
            gap: '8px',
          }}
        >
          {ANSWER_WORDINGS.map((wording) => (
            <OptionCard
              key={wording}
              name={`${baseId}-wording`}
              value={wording}
              checked={value.wording === wording}
              label={t(wordingLabelKey(wording))}
              hint={t(wordingHintKey(wording))}
              hintSize={comfortable ? 13 : 11}
              comfortable={comfortable}
              onSelect={() => onChange({ ...value, wording })}
            />
          ))}
        </Box>
      </Box>

      <Box component="fieldset" sx={{ border: 0, m: 0, p: 0, minWidth: 0 }}>
        <Typography component="legend" sx={[QUESTION_SX, comfortable ? { fontSize: 14 } : {}]}>
          {t('answerStyle.length.question')}
        </Typography>
        <Box
          sx={{
            display: 'grid',
            // Three across where there is room; stacked rows on a phone, so a
            // long translation wraps inside its row instead of crushing a column.
            gridTemplateColumns: { xs: 'minmax(0, 1fr)', sm: 'repeat(3, minmax(0, 1fr))' },
            gap: '8px',
          }}
        >
          {ANSWER_LENGTHS.map((length) => (
            <OptionCard
              key={length}
              name={`${baseId}-length`}
              value={length}
              checked={value.length === length}
              label={t(lengthLabelKey(length))}
              hint={t(lengthHintKey(length))}
              hintSize={comfortable ? 13 : 11}
              comfortable={comfortable}
              onSelect={() => onChange({ ...value, length })}
            />
          ))}
        </Box>
      </Box>

      {afterChoices}

      <Box>
        <Box
          sx={
            exampleToolbarActions
              ? {
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  flexWrap: 'wrap',
                  gap: '8px',
                }
              : undefined
          }
        >
          <Button
            onClick={toggleExample}
            aria-expanded={showExample}
            aria-controls={exampleId}
            endIcon={
              <ChevronDownIcon
                aria-hidden="true"
                style={{
                  width: 12,
                  height: 12,
                  strokeWidth: 2.5,
                  transform: showExample ? 'rotate(180deg)' : 'none',
                }}
              />
            }
            sx={{
              // Shorthand: the global MuiButton padding would otherwise win.
              padding: '0 8px',
              ml: '-8px',
              minHeight: comfortable ? 44 : 32,
              borderRadius: BORDER_RADIUS_PILL,
              textTransform: 'none',
              fontWeight: 600,
              fontSize: comfortable ? 13 : 12,
              color: semanticColors.textPrimary,
              '& .MuiButton-endIcon': { ml: '4px' },
              '&:focus-visible': {
                outline: `2px solid ${semanticColors.brandPrimary}`,
                outlineOffset: '2px',
              },
            }}
          >
            {showExample ? t('answerStyle.example.hide') : t('answerStyle.example.show')}
          </Button>
          {exampleToolbarActions}
        </Box>
        {beforeExample}

        <Box
          id={exampleId}
          role="region"
          aria-label={t('answerStyle.example.badge')}
          hidden={!showExample}
          sx={{
            mt: '8px',
            p: '12px',
            borderRadius: BORDER_RADIUS,
            backgroundColor: semanticColors.surfaceWarm,
            display: showExample ? 'flex' : 'none',
            flexDirection: 'column',
            gap: '8px',
          }}
        >
          <Typography component="span" sx={SECTION_LABEL_SX}>
            {t('answerStyle.example.badge')}
          </Typography>
          <Typography
            sx={{
              alignSelf: 'flex-end',
              maxWidth: '85%',
              px: '10px',
              py: '6px',
              borderRadius: BORDER_RADIUS,
              backgroundColor: semanticColors.surface,
              fontSize: comfortable ? 13 : 12,
              lineHeight: 1.4,
              color: semanticColors.textPrimary,
            }}
          >
            {t('answerStyle.example.question')}
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
            {paragraphs.map((paragraph, index) => (
              <Typography
                key={index}
                sx={{
                  fontSize: comfortable ? 14 : 13,
                  lineHeight: 1.55,
                  color: semanticColors.textBody,
                }}
              >
                {paragraph}
              </Typography>
            ))}
          </Box>
        </Box>
      </Box>

      <Box role="status" aria-live="polite" sx={visuallyHidden}>
        {announcement}
      </Box>
    </Box>
  );
}
